import { useEffect, useState } from "react";
import MetaAppSecurityEditor from "@/components/MetaAppSecurityEditor";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MessageCircle, Plus, Save, Trash2, PlayCircle, CheckCircle2, XCircle, X, Eye, EyeOff, Zap, RefreshCw, ExternalLink, AlertTriangle } from "lucide-react";

/** Provider metadata — every visible copy the operator sees comes from here. */
const KIND_META = {
  email_resend: {
    label: "Resend (transactional email)",
    icon: <Mail className="h-4 w-4" />,
    color: "bg-orange-50 text-orange-700 border-orange-200",
    channel: "email",
    key_hint: "Create an API key at resend.com → Dashboard → API Keys. Starts with 're_'.",
    docs: "https://resend.com/api-keys",
    requires_key: true,
    extra_fields: [
      { key: "from_email", label: "From address", placeholder: "pippy@prephacker.com", required: true,
        hint: "Must be on a domain you've verified in Resend → Dashboard → Domains." },
    ],
  },
  email_mock: {
    label: "Email mock (dev, no send)",
    icon: <Mail className="h-4 w-4" />,
    color: "bg-slate-100 text-slate-700 border-slate-300",
    channel: "email",
    key_hint: "Prints messages to server logs instead of delivering them. Useful during development.",
    requires_key: false,
    extra_fields: [],
  },
  whatsapp_meta: {
    label: "Meta WhatsApp Business Cloud",
    icon: <MessageCircle className="h-4 w-4" />,
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
    channel: "whatsapp",
    key_hint: "Get the permanent access token from developers.facebook.com → WhatsApp Cloud API.",
    docs: "https://developers.facebook.com/docs/whatsapp/cloud-api",
    requires_key: true,
    extra_fields: [
      { key: "phone_number_id", label: "Phone number ID", placeholder: "1234567890", required: true,
        hint: "Found under WhatsApp → API Setup → Phone number ID." },
      { key: "waba_id", label: "WhatsApp Business Account ID (WABA ID)", placeholder: "123456789012345", required: false,
        hint: "Required for the 'Sync Meta Templates' button. Find it at business.facebook.com → WhatsApp Manager (in the page URL after '?waba_id='). Leave blank to let Connect auto-discover (needs whatsapp_business_management permission on the access token)." },
      { key: "webhook_verify_token", label: "Webhook verify token", placeholder: "your-secret-token", required: false,
        hint: "The token you set on the Meta webhook subscription page." },
    ],
  },
  whatsapp_mock: {
    label: "WhatsApp mock (dev, no send)",
    icon: <MessageCircle className="h-4 w-4" />,
    color: "bg-slate-100 text-slate-700 border-slate-300",
    channel: "whatsapp",
    key_hint: "Prints messages to server logs instead of delivering them. Useful during development.",
    requires_key: false,
    extra_fields: [],
  },
};

function WabaIdInlineEditor({ connector, onSaved }) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const trimmed = value.trim();
    if (!/^\d{10,20}$/.test(trimmed)) {
      toast.error("WABA ID should be a numeric ID (usually 15 digits)");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/messaging-connectors/${connector.id}`, {
        extra_config: { ...(connector.extra_config || {}), waba_id: trimmed },
      });
      toast.success("WABA ID saved — you can now Sync Meta Templates");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };
  return (
    <div
      className="mt-3 rounded border border-amber-200 bg-amber-50 p-2.5"
      data-testid={`waba-id-editor-${connector.id}`}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold text-amber-900">
            WABA ID missing — required for the &lsquo;Sync Meta Templates&rsquo; button
          </div>
          <div className="text-[11px] text-amber-800 opacity-90 mb-2">
            Find it at{" "}
            <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer"
               className="underline font-semibold">
              business.facebook.com
            </a>
            {" "}→ WhatsApp Manager → in the page URL after{" "}
            <code className="font-mono bg-white/60 px-1 rounded">?waba_id=</code>{" "}
            (15-digit number).
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              inputMode="numeric"
              placeholder="e.g. 123456789012345"
              value={value}
              onChange={(e) => setValue(e.target.value.replace(/\D/g, ""))}
              className="flex-1 px-2 py-1 text-xs font-mono border border-amber-300 rounded bg-white focus:border-amber-500 outline-none"
              data-testid={`waba-id-input-${connector.id}`}
            />
            <button
              onClick={save}
              disabled={saving || !value.trim()}
              className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50"
              data-testid={`waba-id-save-${connector.id}`}
            >
              <Save className="h-3 w-3" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


export default function MessagingConnectors() {
  const [rows, setRows] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [addKind, setAddKind] = useState("email_resend");
  const [addName, setAddName] = useState("");
  const [addKey, setAddKey] = useState("");
  const [addExtra, setAddExtra] = useState({});
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState("");
  const [testRecipient, setTestRecipient] = useState({});
  const [confirmingDelete, setConfirmingDelete] = useState("");  // holds row.id when armed; second click within 5s fires

  const load = async () => {
    try { const r = await api.get("/messaging-connectors"); setRows(r.data); }
    catch { toast.error("Failed to load connectors"); }
  };
  useEffect(() => { load(); }, []);

  const meta = KIND_META[addKind] || {};

  const submit = async () => {
    if (!addName.trim()) return toast.error("Give the connector a name");
    if (meta.requires_key && !addKey.trim()) return toast.error("API key is required for this provider");
    for (const f of meta.extra_fields || []) {
      if (f.required && !(addExtra[f.key] || "").trim()) return toast.error(`${f.label} is required`);
    }
    setSaving(true);
    try {
      await api.post("/messaging-connectors", {
        kind: addKind, name: addName.trim(), api_key: addKey.trim(), extra_config: addExtra,
      });
      toast.success("Connector added");
      setShowAdd(false); setAddKind("email_resend"); setAddName(""); setAddKey(""); setAddExtra({});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const activate = async (row) => {
    try {
      await api.post(`/messaging-connectors/${row.id}/activate`);
      toast.success(`Activated ${row.name} for ${row.channel}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to activate"); }
  };

  const remove = async (row) => {
    // Two-click confirm — first click arms, second within 5s deletes.
    // Replaces window.confirm() which is blocked inside the preview iframe.
    if (confirmingDelete !== row.id) {
      setConfirmingDelete(row.id);
      setTimeout(() => setConfirmingDelete((cur) => (cur === row.id ? "" : cur)), 5000);
      return;
    }
    setConfirmingDelete("");
    try {
      await api.delete(`/messaging-connectors/${row.id}`);
      toast.success(`Deleted "${row.name}"`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  const runTest = async (row) => {
    const rcpt = (testRecipient[row.id] || "").trim();
    if (!rcpt) return toast.error("Enter a recipient first");
    setTestingId(row.id);
    try {
      const { data } = await api.post(`/messaging-connectors/${row.id}/test`, {
        recipient: rcpt,
      });
      if (data.ok) toast.success(`Sent (${data.latency_ms}ms) — ${data.provider_response?.id || "delivered"}`);
      else toast.error(`Test failed: ${data.error || "unknown error"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTestingId(""); }
  };

  const byChannel = { email: rows.filter((r) => r.channel === "email"), whatsapp: rows.filter((r) => r.channel === "whatsapp") };

  return (
    <div data-testid="messaging-connectors-root" className="p-6 lg:p-10 max-w-[1400px]">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Messaging Connectors</h1>
          <p className="text-sm text-slate-600 mt-1">
            Wire up your Email &amp; WhatsApp providers here. Exactly one connector is <b>Active</b> per channel — that&apos;s the one used by cadences, AI drafts and digests.
          </p>
        </div>
        <button
          data-testid="mc-add-btn"
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-md bg-orange-600 text-white hover:bg-orange-500"
        >
          <Plus className="h-4 w-4" /> Add connector
        </button>
      </div>

      {["email", "whatsapp"].map((ch) => (
        <div key={ch} className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            {ch === "email" ? <Mail className="h-4 w-4 text-slate-500" /> : <MessageCircle className="h-4 w-4 text-slate-500" />}
            <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-500">{ch}</h2>
            <span className="text-[10px] text-slate-400">One active</span>
          </div>
          {byChannel[ch].length === 0 ? (
            <div className="border border-dashed border-slate-200 rounded-md px-4 py-6 text-sm text-slate-500 text-center bg-slate-50">
              No {ch} connectors yet. Add one to start sending {ch === "email" ? "emails" : "WhatsApp messages"}.
            </div>
          ) : (
            <div className="space-y-2">
              {byChannel[ch].map((row) => {
                const m = KIND_META[row.kind] || {};
                return (
                  <div key={row.id} data-testid={`mc-row-${row.id}`}
                       className={`border rounded-md p-4 bg-white ${row.is_active ? "border-orange-300 ring-2 ring-orange-100" : "border-slate-200"}`}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold border ${m.color}`}>
                        {m.icon} {m.label || row.kind}
                      </div>
                      <div className="font-semibold text-slate-900">{row.name}</div>
                      {row.is_active && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-orange-700 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5">
                          <Zap className="h-2.5 w-2.5" /> Active
                        </span>
                      )}
                      {row.api_key_preview && (
                        <span className="text-[11px] font-mono text-slate-500">key: {row.api_key_preview} ({row.api_key_length} chars)</span>
                      )}
                      <div className="flex-1" />
                      <div className="flex items-center gap-2">
                        {!row.is_active && (
                          <button
                            data-testid={`mc-activate-${row.id}`}
                            onClick={() => activate(row)}
                            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded bg-orange-600 text-white hover:bg-orange-500"
                          >
                            <Zap className="h-3 w-3" /> Activate
                          </button>
                        )}
                        <button
                          data-testid={`mc-delete-${row.id}`}
                          onClick={() => remove(row)}
                          disabled={row.is_active}
                          className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded border transition-colors ${
                            row.is_active
                              ? "border-slate-200 text-slate-400 cursor-not-allowed"
                              : confirmingDelete === row.id
                                ? "border-red-800 bg-red-800 text-white animate-pulse"
                                : "border-red-200 text-red-700 bg-red-50 hover:bg-red-100"
                          }`}
                          title={row.is_active ? "Deactivate first (only inactive connectors can be deleted)" : (confirmingDelete === row.id ? "Click again to confirm deletion" : "Delete this connector")}
                        >
                          <Trash2 className="h-3 w-3" /> {confirmingDelete === row.id ? "Click to confirm" : "Delete"}
                        </button>
                      </div>
                    </div>

                    {Object.keys(row.extra_config || {}).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                        {Object.entries(row.extra_config).filter(([k]) => !/secret|token|app_binding/i.test(k)).map(([k, v]) => (
                          <span key={k} className="font-mono bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 text-slate-600">
                            {k}: <span className="text-slate-800">{String(v)}</span>
                          </span>
                        ))}
                      </div>
                    )}

                    {row.kind === "whatsapp_meta" && <>
                      <div className="text-xs mt-2">Webhook verification token: {row.webhook_verify_token_configured ? "Configured" : "Not stored on connector"}</div>
                      <MetaAppSecurityEditor connector={row} onSaved={load} />
                    </>}

                    {/* v25 — inline WABA ID editor for existing WhatsApp Meta connectors.
                        Auto-appears when the active connector lacks a waba_id (or has only
                        the legacy business_account_id key). Fixes the 'Sync Meta Templates'
                        502 in one click without ops having to touch Mongo. */}
                    {row.kind === "whatsapp_meta" && !((row.extra_config || {}).waba_id) && (
                      <WabaIdInlineEditor
                        connector={row}
                        onSaved={load}
                      />
                    )}

                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Send test</span>
                      <input
                        data-testid={`mc-test-input-${row.id}`}
                        placeholder={ch === "email" ? "you@example.com" : "+91XXXXXXXXXX"}
                        value={testRecipient[row.id] || ""}
                        onChange={(e) => setTestRecipient({ ...testRecipient, [row.id]: e.target.value })}
                        className="flex-1 max-w-xs px-2 py-1 text-xs border border-slate-200 rounded bg-white focus:border-orange-500 outline-none font-mono"
                      />
                      <button
                        data-testid={`mc-test-btn-${row.id}`}
                        onClick={() => runTest(row)}
                        disabled={testingId === row.id}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-60"
                      >
                        {testingId === row.id ? <RefreshCw className="h-3 w-3 animate-spin" /> : <PlayCircle className="h-3 w-3" />}
                        Test
                      </button>
                      {row.last_test_status && (
                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${row.last_test_status === "ok" ? "text-emerald-700" : "text-red-700"}`}>
                          {row.last_test_status === "ok" ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                          Last: {row.last_test_status} · {row.last_test_ms}ms
                          {row.last_test_recipient && <span className="text-slate-500 font-normal">→ {row.last_test_recipient}</span>}
                        </span>
                      )}
                      {row.last_test_error && (
                        <div className="w-full text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mt-1 font-mono break-all">
                          {row.last_test_error}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}

      {/* Add-connector modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-lg max-w-xl w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-slate-900">Add messaging connector</h3>
              <button onClick={() => setShowAdd(false)} className="text-slate-400 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">Provider</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(KIND_META).map(([k, m]) => (
                    <button
                      key={k}
                      data-testid={`mc-kind-${k}`}
                      onClick={() => setAddKind(k)}
                      className={`text-left p-3 rounded-md border transition-colors ${
                        addKind === k ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${m.color}`}>
                        {m.icon} {m.channel}
                      </div>
                      <div className="text-sm font-semibold text-slate-900 mt-1">{m.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">Display name</label>
                <input
                  data-testid="mc-name-input"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  placeholder="e.g. Resend production"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
                />
              </div>

              {meta.requires_key && (
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">API key</label>
                  <div className="relative">
                    <input
                      data-testid="mc-key-input"
                      type={showKey ? "text" : "password"}
                      value={addKey}
                      onChange={(e) => setAddKey(e.target.value)}
                      placeholder={meta.channel === "email" ? "re_..." : "EAA..."}
                      className="w-full px-3 py-2 pr-10 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none font-mono"
                    />
                    <button
                      onClick={() => setShowKey(!showKey)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    >
                      {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {meta.key_hint && (
                    <div className="text-[11px] text-slate-500 mt-1 flex items-start gap-1">
                      {meta.key_hint}
                      {meta.docs && (
                        <a href={meta.docs} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-orange-600 font-semibold hover:underline">
                          Open docs <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}

              {(meta.extra_fields || []).map((f) => (
                <div key={f.key}>
                  <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">
                    {f.label}{f.required && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    data-testid={`mc-extra-${f.key}`}
                    type={/token|secret/i.test(f.key) ? "password" : "text"}
                    value={addExtra[f.key] || ""}
                    onChange={(e) => setAddExtra({ ...addExtra, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none font-mono"
                  />
                  {f.hint && <div className="text-[11px] text-slate-500 mt-1">{f.hint}</div>}
                </div>
              ))}

              {!meta.requires_key && (
                <div className="text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2 text-slate-600">
                  {meta.key_hint}
                </div>
              )}
            </div>
            <div className="p-5 border-t border-slate-200 flex items-center justify-end gap-2">
              <button onClick={() => setShowAdd(false)} className="text-sm font-semibold px-3 py-1.5 rounded border border-slate-200 text-slate-700 hover:bg-slate-50">
                Cancel
              </button>
              <button
                data-testid="mc-save-btn"
                onClick={submit}
                disabled={saving}
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded bg-orange-600 text-white hover:bg-orange-500 disabled:opacity-60"
              >
                {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Add connector
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
